<template>
  <div>
    <div v-if="isLoadingSummary" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Loading...</span>
      </div>
      <p class="mt-2 text-muted">Loading summary data...</p>
    </div>

    <div v-if="!isLoadingSummary" class="completion-summary-wrapper mb-4">
      <div class="completion-overview-card">
        <div class="completion-summary-header">
          <div>
            <span class="summary-eyebrow">This Year</span>
            <h5 class="mb-0">IDP Course Completion Summary</h5>
          </div>
          <div class="summary-rate">
            <strong>{{ currentCompletionPercentage }}%</strong>
            <span>completion progress</span>
          </div>
        </div>

        <div class="progress mb-3" style="height: 18px">
          <div
            class="progress-bar bg-primary"
            role="progressbar"
            :style="`width: ${currentCompletionPercentage}%;`"
            :aria-valuenow="currentCompletionPercentage"
            aria-valuemin="0"
            aria-valuemax="100"
          >
            {{ currentCompletionPercentage }}%
          </div>
        </div>

        <div class="summary-card-grid">
          <div class="status-card bg-teal">
            <div class="d-flex align-items-center justify-content-center mb-2">
              <div class="status-icon">
                <i class="fe fe-check-circle"></i>
              </div>
            </div>
            <div class="text-center text-white">
              <p class="mb-1">Enrolled</p>
              <h3 class="mb-0">{{ currentSummary.enrolled }}</h3>
            </div>
          </div>

          <div class="status-card bg-danger">
            <div class="d-flex align-items-center justify-content-center mb-2">
              <div class="status-icon">
                <i class="fe fe-file-text"></i>
              </div>
            </div>
            <div class="text-center text-white">
              <p class="mb-1">In Review</p>
              <h3 class="mb-0">{{ currentSummary.in_review }}</h3>
            </div>
          </div>

          <div class="status-card bg-success">
            <div class="d-flex align-items-center justify-content-center mb-2">
              <div class="status-icon">
                <i class="fe fe-check"></i>
              </div>
            </div>
            <div class="text-center text-white">
              <p class="mb-1">Completed</p>
              <h3 class="mb-0">{{ currentSummary.completed }}</h3>
            </div>
          </div>
        </div>
      </div>

      <div class="history-card">
        <span class="summary-eyebrow">Last Year</span>
        <h5 class="mb-3">IDP Completion</h5>
        <div class="history-list">
          <div>
            <span>Enrolled</span>
            <strong>{{ previousSummary.enrolled }}</strong>
          </div>
          <div>
            <span>In Review</span>
            <strong>{{ previousSummary.in_review }}</strong>
          </div>
          <div>
            <span>Completed</span>
            <strong>{{ previousSummary.completed }}</strong>
          </div>
        </div>
        <div class="history-progress">
          <div :style="`width: ${previousCompletionPercentage}%;`"></div>
        </div>
        <small>{{ previousCompletionPercentage }}% completion progress</small>
      </div>

      <div class="completion-roadmap-card">
        <div class="completion-summary-header">
          <div>
            <span class="summary-eyebrow">Roadmap</span>
            <h5 class="mb-0">Proses Completion IDP</h5>
          </div>
        </div>

        <div class="roadmap-lanes">
          <div class="roadmap-lane">
            <div class="lane-heading">
              <span>Internal</span>
            </div>
            <div class="completion-roadmap">
              <div v-for="step in internalSteps" :key="step.label" class="roadmap-step">
                <div class="roadmap-marker">
                  <i :class="step.icon"></i>
                </div>
                <span>{{ step.label }}</span>
              </div>
            </div>
          </div>

          <div class="roadmap-lane">
            <div class="lane-heading">
              <span>External</span>
            </div>
            <div class="completion-roadmap">
              <div v-for="step in externalSteps" :key="step.label" class="roadmap-step">
                <div class="roadmap-marker">
                  <i :class="step.icon"></i>
                </div>
                <span>{{ step.label }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "Summary",
  props: {
    isLoadingSummary: {
      type: Boolean,
      default: false,
    },
    summaryData: {
      type: Object,
      default: () => ({
        previous: { enrolled: 0, in_review: 0, completed: 0, percentage: 0 },
        current: { enrolled: 0, in_review: 0, completed: 0, percentage: 0 },
      }),
    },
  },
  computed: {
    currentSummary() {
      return this.normalizeSummary(this.summaryData?.current);
    },
    previousSummary() {
      return this.normalizeSummary(this.summaryData?.previous);
    },
    currentCompletionPercentage() {
      return this.calculateCompletionPercentage(this.currentSummary);
    },
    previousCompletionPercentage() {
      return this.calculateCompletionPercentage(this.previousSummary);
    },
    internalSteps() {
      return [
        { label: "Enrolled", icon: "fe fe-check-circle" },
        { label: "Training Process", icon: "fe fe-play-circle" },
        { label: "Completed", icon: "fe fe-check" },
      ];
    },
    externalSteps() {
      return [
        { label: "Enrolled", icon: "fe fe-check-circle" },
        { label: "Certificate Review", icon: "fe fe-file-text" },
        { label: "Completed", icon: "fe fe-check" },
      ];
    },
  },
  methods: {
    normalizeSummary(summary = {}) {
      const enrolled = Number(summary?.enrolled ?? summary?.approved ?? 0);
      const inReview = Number(summary?.in_review || 0);
      const completed = Number(summary?.completed || 0);

      return {
        enrolled: enrolled + inReview + completed,
        in_review: inReview,
        completed,
        total: Number(summary?.total || 0),
        percentage: Number(summary?.percentage || 0),
      };
    },
    calculateCompletionPercentage(summary) {
      if (!summary.enrolled) return 0;

      return Math.min(Math.round((summary.completed / summary.enrolled) * 100), 100);
    },
  },
};
</script>

<style>
.completion-summary-wrapper {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 260px;
  gap: 16px;
}

.completion-overview-card,
.history-card,
.completion-roadmap-card {
  border: 1px solid #e6edf7;
  border-radius: 8px;
  padding: 16px;
  background: #fff;
  box-shadow: 0 8px 24px rgba(31, 45, 61, 0.04);
}

.completion-roadmap-card {
  grid-column: 1 / -1;
}

.completion-summary-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.summary-eyebrow {
  display: block;
  color: #90a4c4;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: uppercase;
  margin-bottom: 4px;
}

.summary-rate {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  color: #5e6b7f;
}

.summary-rate strong {
  color: #b80000;
  font-size: 22px;
  line-height: 1;
}

.summary-rate span,
.history-card small {
  font-size: 12px;
  font-weight: 700;
}

.completion-summary-wrapper .summary-card-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(150px, 1fr));
  gap: 12px;
}

.completion-summary-wrapper .status-card {
  border-radius: 8px;
  padding: 16px;
  min-height: 112px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.completion-summary-wrapper .status-icon {
  width: 44px;
  height: 44px;
  background-color: rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: white;
}

.completion-summary-wrapper .status-card p {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 0;
}

.completion-summary-wrapper .status-card h3 {
  font-size: 30px;
  font-weight: bold;
  margin: 0;
}

.bg-teal {
  background-color: #5bc0be;
}

.progress {
  background-color: #e9ecef;
  border-radius: 0.5rem;
  overflow: hidden;
}

.progress-bar {
  font-weight: 700;
  font-size: 12px;
}

.history-list {
  display: grid;
  gap: 10px;
  margin-bottom: 16px;
}

.history-list div {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #edf1f6;
  padding-bottom: 8px;
}

.history-list span {
  color: #5e6b7f;
  font-size: 12px;
  font-weight: 700;
}

.history-list strong {
  color: #14213d;
  font-size: 22px;
  line-height: 1;
}

.history-progress {
  height: 8px;
  border-radius: 8px;
  background: #edf1f6;
  overflow: hidden;
  margin-bottom: 8px;
}

.history-progress div {
  height: 100%;
  border-radius: inherit;
  background: #b80000;
}

.roadmap-lanes {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.roadmap-lane {
  min-width: 0;
}

.lane-heading {
  margin-bottom: 10px;
}

.lane-heading span {
  color: #14213d;
  font-size: 14px;
  font-weight: 700;
}

.completion-roadmap {
  position: relative;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  padding-top: 6px;
}

.completion-roadmap::before {
  content: "";
  position: absolute;
  top: 21px;
  left: 14px;
  right: 14px;
  height: 2px;
  background: #dbe4f0;
}

.roadmap-step {
  position: relative;
  z-index: 1;
  min-width: 0;
}

.roadmap-marker {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2px solid #08ce79;
  background: #eafff5;
  color: #08a867;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 6px;
  font-size: 12px;
}

.roadmap-step span {
  display: block;
  color: #14213d;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.25;
}

@media (max-width: 991.98px) {
  .completion-summary-wrapper,
  .roadmap-lanes,
  .completion-roadmap {
    grid-template-columns: 1fr;
  }

  .completion-roadmap::before {
    display: none;
  }

  .summary-rate {
    align-items: flex-start;
  }
}

@media (max-width: 575.98px) {
  .completion-summary-header {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
